import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Users, Eye, EyeOff, Award, Save } from "lucide-react";

function MyProfile() {
  const [p, setP] = useState(null);
  const [skillsText, setSkillsText] = useState("");

  useEffect(() => {
    api.get("/talent/me").then((r) => { setP(r.data); setSkillsText((r.data.skills || []).join(", ")); }).catch(() => {});
  }, []);

  if (!p) return <div className="py-10 text-center text-slate-500">Loading…</div>;
  const set = (k) => (e) => setP({ ...p, [k]: e.target.value });

  const save = async () => {
    try {
      const skills = skillsText.split(",").map((s) => s.trim()).filter(Boolean);
      const { data } = await api.put("/talent/me", { display_name: p.display_name, headline: p.headline, bio: p.bio, skills, visible: p.visible });
      setP(data);
      toast.success("Profile saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };

  const inp = "mt-2 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-slate-900 outline-none focus:border-[#E61A27]";

  return (
    <div className="axiom-card p-6" data-testid="my-talent-profile">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-xl text-slate-900">Your portfolio</h3>
        <button data-testid="visibility-toggle" onClick={() => setP({ ...p, visible: !p.visible })}
          className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm ${p.visible ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-300 text-slate-500"}`}>
          {p.visible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
          {p.visible ? "Discoverable" : "Hidden"}
        </button>
      </div>
      <p className="mt-1 text-sm text-slate-500">Consent-based: institutions can only discover you while this profile is set to discoverable.</p>

      <label className="eyebrow mt-4 block">Display name</label>
      <input data-testid="profile-name" value={p.display_name} onChange={set("display_name")} className={inp} />
      <label className="eyebrow mt-4 block">Headline</label>
      <input data-testid="profile-headline" value={p.headline} onChange={set("headline")} placeholder="e.g. Analysis & number theory specialist" className={inp} />
      <label className="eyebrow mt-4 block">Bio</label>
      <textarea data-testid="profile-bio" rows={3} value={p.bio} onChange={set("bio")} className={inp} />
      <label className="eyebrow mt-4 block">Skills (comma separated)</label>
      <input data-testid="profile-skills" value={skillsText} onChange={(e) => setSkillsText(e.target.value)} placeholder="Topology, PDEs, Combinatorics" className={inp} />

      <div className="mt-5">
        <p className="eyebrow mb-2">Credited work (from accepted submissions)</p>
        <div className="space-y-2">
          {(p.credited_work || []).map((w, i) => (
            <div key={i} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700" data-testid={`credit-${i}`}>
              <Award className="h-4 w-4 text-[#D4AF37]" /> {w.problem_title}
            </div>
          ))}
          {(!p.credited_work || p.credited_work.length === 0) && <p className="text-sm text-slate-500">No credited work yet — accepted submissions appear here.</p>}
        </div>
      </div>

      <button data-testid="profile-save" onClick={save} className="btn-crimson mt-5 !py-2 text-sm"><Save className="h-4 w-4" /> Save profile</button>
    </div>
  );
}

export default function Talent() {
  const { user } = useAuth();
  const [profiles, setProfiles] = useState([]);

  useEffect(() => { api.get("/talent/profiles").then((r) => setProfiles(r.data)).catch(() => {}); }, []);

  return (
    <div className="mx-auto max-w-5xl px-5 py-12 sm:px-8" data-testid="talent-page">
      <p className="eyebrow mb-2">Talent</p>
      <h1 className="font-display text-3xl tracking-tight text-slate-900 sm:text-4xl flex items-center gap-3"><Users className="h-8 w-8 text-[#E61A27]" /> Talent Directory</h1>
      <p className="mt-3 max-w-2xl text-slate-600">Consent-based portfolios built from accepted, credited work. Institutions discover contributors who opt in.</p>

      {user?.role === "contributor" && <div className="mt-8"><MyProfile /></div>}

      <div className="mt-10">
        <p className="eyebrow mb-3">Discoverable contributors</p>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          {profiles.map((p) => (
            <div key={p.user_id} className="axiom-card p-6" data-testid={`talent-${p.user_id}`}>
              <h3 className="font-display text-xl text-slate-900">{p.display_name}</h3>
              {p.headline && <p className="mt-1 text-sm text-[#B8860B]">{p.headline}</p>}
              {p.bio && <p className="mt-2 text-sm text-slate-600">{p.bio}</p>}
              {p.skills?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {p.skills.map((s) => <span key={s} className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-xs text-slate-600">{s}</span>)}
                </div>
              )}
              <p className="mt-3 text-xs text-slate-400">{(p.credited_work || []).length} credited accepted work(s)</p>
            </div>
          ))}
        </div>
        {profiles.length === 0 && <p className="mt-2 text-slate-500" data-testid="talent-empty">No discoverable profiles yet.</p>}
      </div>
    </div>
  );
}
